// Core Modules
import { Component, OnInit, OnDestroy, AfterContentChecked, ChangeDetectorRef } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgClass, AsyncPipe } from '@angular/common';

// External Modules
import { BehaviorSubject, filter, combineLatest, map, Subject, takeUntil } from 'rxjs';

// Services
import { AuthService } from '@core/services/auth/auth.service';

// Components
import { HeaderComponent } from '@shared/components/header/header.component';
import { FooterComponent } from '@shared/components/footer/footer.component';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

/**
 * App Component
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [HeaderComponent, NgClass, RouterOutlet, FooterComponent, AsyncPipe],
})
export class AppComponent implements OnInit, AfterContentChecked, OnDestroy {
  /**
   * Subject to handle component destruction and cleanup subscriptions
   */
  private readonly destroy$ = new Subject<void>();

  /**
   * Hide Navigation Subject
   */
  public readonly hideNavigationSubject = new BehaviorSubject<boolean>(true);

  /**
   * Combined application state observable that manages authentication, navigation, and UI visibility states.
   * Reduces template complexity by combining multiple observables into a single stream.
   */
  public readonly appState$ = combineLatest([this.auth.isSessionValid$, this.auth.isAuthenticationInProgress$, this.auth.isClientNavigationInProgress$, this.hideNavigationSubject.asObservable()]).pipe(
    takeUntil(this.destroy$),
    map(([isSessionValid, isAuthInProgress, isClientNavInProgress, hideNavigation]) => ({
      isSessionValid,
      isAuthInProgress,
      isClientNavInProgress,
      hideNavigation,
      showHeader: !hideNavigation,
      showContent: !isClientNavInProgress,
      showFooter: !hideNavigation,
    }))
  );

  /**
   * The constructor method
   * @param auth auth
   * @param sessionManager session manager
   * @param router router
   */
  constructor(
    public readonly auth: AuthService,
    private readonly router: Router,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  /**
   * A callback method that is invoked immediately after the default change detector has checked the directive's data-bound properties for the first time, and before any of the view or content children have been checked.
   */
  public ngOnInit(): void {
    this.router.events
      .pipe(
        filter((i) => i instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (event: any) => {
          if ([`/${MainNavigationEnum.LOGIN}`, '/'].includes(event.urlAfterRedirects)) {
            this.hideNavigationSubject.next(true);
          } else {
            this.hideNavigationSubject.next(false);
          }
        },
        error: (e) => {
          console.log(`<AppComponent> - <ngOnInit> Error occurred during subscribing router events...`, e);
          this.hideNavigationSubject.next(false);
        },
      });
  }

  /**
   * Respond after Angular checks the content projected into the directive or component.
   */
  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  /**
   * A callback method that performs custom clean-up, invoked immediately before an instance is destroyed.
   */
  public ngOnDestroy(): void {
    // Trigger completion for all observables using takeUntil
    this.destroy$.next();
    this.destroy$.complete();

    // Complete the BehaviorSubject to prevent memory leaks
    this.hideNavigationSubject.complete();
  }
}
